import { h, VNode } from "preact";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { useCommandHandler, useForceUpdate } from "../hooks";
import { Command } from "../hooks/Command";

export const predefinedMetadataKeys: {
    [key: string]: {
        values?: string[],
        autofill?: string[]
    }
} = {
    "high-quality": {
        values: ["true", "false", "auto"]
    },
    "tape-width": {
        values: ["6mm", "9mm", "12mm", "18mm", "24mm", "36mm"]
    },
    "tape-type": {
        values: [
            "black ink on white tape, laminated",
            "black ink on clear tape, laminated",
            "black ink on white tape, security",
            "black ink on white tape, strong adhesive",
            "blue ink on white tape, laminated",
            "white ink on black tape, laminated",
            "black ink on clear tape, strong adhesive",
            "black ink on green tape, laminated",
            "black ink on matte clear tape, laminated",
            "black ink on red tape, laminated",
            "black ink on white tape, starry laminated",
            "blue ink on white tape, fabric tape",
            "black ink on white tape, flexible id",
            "black ink on white tape, security laminated",
            "black ink on fluorescent yellow tape, laminated",
            "white ink on clear tape, laminated",
            "blue ink on clear tape, laminated",
            "white ink on red tape, laminated",
            "white ink on blue tape, laminated",
        ].sort()
    },
    "printer": {
        values: ["Brother P900W"]
    },
    "field-": {
        autofill: [
            "text",
            "date",
            "serial-8-base32",
            "serial-6-base16",
            "text-random",
            "combobox",
            "number",
            "combobox-csv",
            "gtin-lot",
            "gtin-lot-8-date"
        ]
    },
    "label-length": {

    }
};

function getPredefinedAutoFillName(key: string) {
    if (key.startsWith("field-")) {
        return predefinedMetadataKeys["field-"].autofill ? "field-" : null;
    }

    if (key in predefinedMetadataKeys) {
        return predefinedMetadataKeys[key].autofill ? key : null;
    }

    return null;
}

export function TemplateMetadataDisplay(props: {
    template?: { metadata: { [key: string]: string } } | null,
    loadFromStorage?: boolean,
    onChange: (metadata: { [key: string]: string }) => void,
    doClear?: Command,
    doSetMetadata?: Command<[ { [key: string]: string }]>;
}) {
    const forceUpdate = useForceUpdate();
    const [newKey, setNewKey] = useState("");
    const [newValue, setNewValue] = useState("");
    const [metadata, setMetadata] = useState(props.template ? props.template.metadata : {});

    useEffect(() => {
        if (!props.loadFromStorage) {
            return;
        }

        const metadata = localStorage.getItem("metadata");

        if (!metadata) {
            return;
        }

        const obj = JSON.parse(metadata);
        setMetadata(obj);
        props.onChange(obj);
    }, [true]);

    useCommandHandler(props.doClear, () => {
        for (const prop in  metadata) {
            delete metadata[prop];
        }

        localStorage.setItem("metadata", JSON.stringify(metadata));
        props.onChange(metadata);

        forceUpdate();
    });

    useCommandHandler(props.doSetMetadata, (metadata) => {
        setMetadata(metadata);
        props.onChange(metadata);
        localStorage.setItem("metadata", JSON.stringify(metadata));

        if (props.template) {
            props.template.metadata = metadata;
        }
    });

    useEffect(() => {
        // if (props.template && Object.keys(props.template.metadata).length > 0) {
        //     setMetadata(props.template.metadata)
        //     localStorage.setItem("metadata", JSON.stringify(props.template.metadata));
        //     props.onChange(props.template.metadata);
        // }
        // else {
        //     if (props.template) {
        //         props.template.metadata = metadata;
        //         localStorage.setItem("metadata", JSON.stringify(metadata));
        //         props.onChange(metadata);
        //     }
        // }

        setMetadata(props.template ? props.template.metadata : {});
    }, [props.template]);

    const highlighterCallback = useCallback((key: string, t: string) => {
        // const regex = /{.*?(?:}\s*[^}]|}$)/gm;

        // let match;

        // while(match = regex.exec(t)) {

        // }

        let parts = [];

        let start = 0;
        let end = 0;
        let braceParse = false;

        if (key.startsWith("field-")) {
            const s = t.split(",")[0];

            if (predefinedMetadataKeys["field-"].autofill!.indexOf(s.trim()) >= 0) {
                parts.push(<span style="color: rgb(100, 180, 255)">{s}</span>);
                start = s.length;
            }
        }

        for (let i = 0; i < t.length; i++) {
            end = i;

            if (t[i] === "{" && !braceParse) {
                parts.push(<span>{t.substring(start, end)}</span>);
                start = i;
                braceParse = true;
            }
            else if (t[i] === "}") {
                parts.push(<span style="color: rgb(255, 180, 100)">{t.substring(start, end + 1)}</span>);
                start = i + 1;
                braceParse = false;
            }
        }

        end = t.length;

        if (end - start > 0) {
            if (key in predefinedMetadataKeys && predefinedMetadataKeys[key].values && predefinedMetadataKeys[key].values!.indexOf(t) >= 0) {
                parts.push(<span style="color: rgb(100, 180, 255)">{t.substring(start, end)}</span>);
            }
            else {
                parts.push(<span>{t.substring(start, end)}</span>);
            }
        }

        return parts;
    }, [true]);

    const autoFills = [];

    for (const prop in predefinedMetadataKeys) {
        const current = predefinedMetadataKeys[prop];

        if (current.autofill) {
            autoFills.push(<datalist id={"tmd-datalist-" + prop}>
                    {current.autofill.map(v => <option value={v}>{v}</option>)}
                </datalist>
            );
        }
    }

    return <div>
        <div style="display: none">{autoFills}</div>
        <div class="template-metadata-component">
            <div style="font-weight: bold; background: rgba(255, 255, 255, 0.1)">Klucz</div>
            <div style="font-weight: bold; background: rgba(255, 255, 255, 0.1)">Wartość</div>
            {Object.entries(metadata).map(([key, value]) => [
                <div><DblClickEditableInput value={key} onChange={(value) => {
                    if (metadata[value]) {
                        return;
                    }

                    metadata[value] = metadata[key];
                    delete metadata[key];

                    props.onChange(metadata);
                    localStorage.setItem("metadata", JSON.stringify(metadata));
                    forceUpdate();
                }}/><button type="button" class="delete icon error flat" onClick={() => {
                    delete metadata[key];
                    props.onChange(metadata);
                    localStorage.setItem("metadata", JSON.stringify(metadata));
                    forceUpdate();
                }}>close</button></div>,
                key in predefinedMetadataKeys && predefinedMetadataKeys[key].values ?
                <DblClickEditableComboBox obj={metadata} prop={key} onChange={() => {
                    props.onChange(metadata);
                    localStorage.setItem("metadata", JSON.stringify(metadata));
                }} highlighter={highlighterCallback} items={predefinedMetadataKeys[key].values!}/>
                : <DblClickEditableInput value={metadata[key]} highlighterKey={key} onChange={(value) => {
                    metadata[key] = value;
                    props.onChange(metadata);
                    localStorage.setItem("metadata", JSON.stringify(metadata));
                }} highlighter={highlighterCallback}
                autofill={getPredefinedAutoFillName(key) ? "tmd-datalist-" + getPredefinedAutoFillName(key) : undefined}/>,
            ])}
            <div class="new key"><input spellcheck={false} placeholder="Nazwa klucza" value={newKey} onChange={(e) => setNewKey(e.currentTarget.value)} onPaste={(e) => {
                if (!e.clipboardData) {
                    return;
                }

                const text = e.clipboardData.getData("text");
                const lines = text.split("\n");

                for (let i = 0; i < lines.length; i++) {
                    const line = lines[i];
                    const split = line.split("=");

                    if (split.length === 1) {
                        return;
                    }

                    if (split.length >= 2) {
                        const name = split[0].trim();
                        const value = split.slice(1).join("=").trim();

                        metadata[name] = value;
                    }
                }

                props.onChange(metadata);
                localStorage.setItem("metadata", JSON.stringify(metadata));
                e.currentTarget.blur();
                forceUpdate();
            }}/></div>
            <div class="new">
                { newKey in predefinedMetadataKeys && predefinedMetadataKeys[newKey].values ?
                    <select
                        spellcheck={false}
                        value={"true"}
                        onChange={(e) => {
                            e.currentTarget.blur();

                            metadata[newKey] = e.currentTarget.value;
                            props.onChange(metadata);
                            localStorage.setItem("metadata", JSON.stringify(metadata));

                            setNewKey("");
                            setNewValue("");
                        }}>
                        {predefinedMetadataKeys[newKey].values!.map((v) => <option value={v}>{v}</option>)}
                    </select>
                : <input placeholder="Wartość (Enter aby zatwierdzić)"
                    spellcheck={false}
                    value={newValue}
                    onInput={(e) => setNewValue(e.currentTarget.value)}
                    list={getPredefinedAutoFillName(newKey) ? "tmd-datalist-" + getPredefinedAutoFillName(newKey) : undefined}
                    onKeyPress={(e) => {
                    if (e.key === "Enter" && newKey && newValue && !metadata[newKey]) {
                        e.currentTarget.blur();

                        metadata[newKey] = newValue;
                        props.onChange(metadata);
                        localStorage.setItem("metadata", JSON.stringify(metadata));

                        setNewKey("");
                        setNewValue("");
                    }
                }}/>}
            </div>
        </div>
    </div>;
}

function DblClickEditableInput(props: {
    value: string,
    onChange: (value: string) => void,
    highlighter?: (key: string, text: string) => VNode | VNode[],
    autofill?: string ,
    highlighterKey?: string
}) {
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (isEditing && inputRef && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    return !isEditing
        ? <div onDblClick={() => setIsEditing(true)}>{props.highlighter ? props.highlighter(props.highlighterKey!, props.value) : props.value}</div>
        : <input ref={inputRef}
            list={props.autofill}
            spellcheck={false}
            value={props.value}
            onChange={(e) => {
                props.onChange(e.currentTarget.value);
            }} onBlur={() => setIsEditing(false)}
            onKeyPress={(e) => {
                if (e.key === "Enter") {
                    inputRef.current?.blur();
                }
            }}
            />;
}

function DblClickEditableComboBox(props: { obj: { [key: string]: string }, prop: string, onChange: () => void, highlighter?: (key: string, text: string) => VNode | VNode[], items: string[] }) {
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef<HTMLSelectElement | null>(null);

    useEffect(() => {
        if (isEditing && inputRef && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    return !isEditing
        ? <div onDblClick={() => setIsEditing(true)}>{props.highlighter ? props.highlighter(props.prop, props.obj[props.prop]) : props.obj[props.prop]}</div>
        : <select ref={inputRef}
            spellcheck={false}
            value={props.obj[props.prop]}
            onChange={(e) => {
                props.obj[props.prop] = e.currentTarget.value;
                props.onChange();
                inputRef.current?.blur();
            }} onBlur={() => setIsEditing(false)}
            onKeyPress={(e) => {
                if (e.key === "Enter") {
                    inputRef.current?.blur();
                }
            }}>
            {props.items.map((v) => <option value={v}>{v}</option>)}
        </select>;
}
